import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  ListItemText,
  Badge,
  Tooltip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  AccountCircle,
  Logout,
  Settings,
  Notifications,
  Storefront,
  Email,
  VisibilityOutlined,
  QuestionAnswer,
  AddTask,
  TaskAlt,
  BadgeOutlined,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { NotificationCounts } from '../../hooks/useNotificationCounts';
import { QUICK_ANSWER_SHORTCUT } from '../../pages/answers/components/QuickAnswerDialog';
import { taskService } from '../../services/taskService';
import { NameDialog } from '../../pages/tasks/YourNameDialog';
import { useStaffNames } from '../../pages/tasks/useStaffNames';
import { avatarColor, describeNotification, initials, personName, timeAgo } from '../../pages/tasks/taskUtils';

interface HeaderProps {
  open: boolean;
  onMenuClick: () => void;
  notificationCounts: NotificationCounts;
  /** Opens the Answer Desk quick answers; the button is hidden without it. */
  onQuickAnswers?: () => void;
  /** Opens "New task"; the button is hidden without it. */
  onNewTask?: () => void;
}

export const Header: React.FC<HeaderProps> = ({ open, onMenuClick, notificationCounts, onQuickAnswers, onNewTask }) => {
  const { user, logout, updateUser } = useAuth();
  const [nameOpen, setNameOpen] = React.useState(false);
  const { isInvestor } = usePermissions();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [notifAnchorEl, setNotifAnchorEl] = React.useState<null | HTMLElement>(null);
  const { pendingOrderRequests, unreadContactMessages, taskSummary, refreshTaskSummary } = notificationCounts;
  const taskAlerts = taskSummary?.latest_unread ?? [];
  const unreadTaskAlerts = taskSummary?.unread_notifications ?? 0;
  const tasksDue = (taskSummary?.overdue ?? 0) + (taskSummary?.due_today ?? 0);
  const notificationTotal = pendingOrderRequests + unreadContactMessages + unreadTaskAlerts;
  const actorNames = useStaffNames(actorIds(taskAlerts));
  const myName = user ? personName({ display_name: user.display_name ?? null, email: user.email }) : '';

  const openTaskAlert = async (taskId: string, notificationId: string) => {
    handleNotifMenuClose();
    navigate(`/tasks?task=${taskId}`);
    try {
      await taskService.markNotificationsRead([notificationId]);
    } finally {
      refreshTaskSummary();
    }
  };

  const markAllTaskAlertsRead = async () => {
    try {
      await taskService.markNotificationsRead(null);
    } finally {
      refreshTaskSummary();
    }
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleProfileMenuClose();
  };

  const handleNotifMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNotifAnchorEl(event.currentTarget);
  };

  const handleNotifMenuClose = () => {
    setNotifAnchorEl(null);
  };

  const goTo = (path: string) => {
    navigate(path);
    handleNotifMenuClose();
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        zIndex: theme => theme.zIndex.drawer + 1,
        transition: theme => theme.transitions.create(['width', 'margin'], {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen,
        }),
      }}
    >
      <Toolbar>
        <IconButton
          color="inherit"
          aria-label="open drawer"
          onClick={onMenuClick}
          edge="start"
          sx={{ mr: 2 }}
        >
          <MenuIcon />
        </IconButton>
        
        <Typography 
          variant="h6" 
          noWrap 
          component="div" 
          sx={{ 
            flexGrow: 1,
            display: { xs: 'none', sm: 'block' }
          }}
        >
          Catelyn Pet Shop - Admin Dashboard
        </Typography>
        
        <Typography 
          variant="h6" 
          noWrap 
          component="div" 
          sx={{ 
            flexGrow: 1,
            display: { xs: 'block', sm: 'none' }
          }}
        >
          CPS Admin
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
          {isInvestor && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1.5,
                py: 0.5,
                borderRadius: 5,
                bgcolor: 'rgba(255,255,255,0.15)',
              }}
            >
              <VisibilityOutlined fontSize="small" />
              <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' } }}>
                Read-only (Investor)
              </Typography>
            </Box>
          )}
          {!isInvestor && onNewTask && (
            <Tooltip title="New task (T)">
              <IconButton color="inherit" aria-label="new task" onClick={onNewTask}>
                <AddTask />
              </IconButton>
            </Tooltip>
          )}
          {!isInvestor && onQuickAnswers && (
            <Tooltip title={`Quick answers (${QUICK_ANSWER_SHORTCUT})`}>
              <IconButton color="inherit" aria-label="quick answers" onClick={onQuickAnswers}>
                <QuestionAnswer />
              </IconButton>
            </Tooltip>
          )}
          {!isInvestor && (
            <IconButton
              color="inherit"
              sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
              aria-label="notifications"
              aria-controls="notification-menu"
              aria-haspopup="true"
              onClick={handleNotifMenuOpen}
            >
              <Badge badgeContent={notificationTotal} color="error">
                <Notifications />
              </Badge>
            </IconButton>
          )}

          <IconButton
            size="large"
            edge="end"
            aria-label="account of current user"
            aria-controls="profile-menu"
            aria-haspopup="true"
            onClick={handleProfileMenuOpen}
            color="inherit"
          >
            <Avatar sx={{ width: 32, height: 32, bgcolor: user ? avatarColor(user.user_id) : undefined, fontSize: 14, fontWeight: 700 }}>
              {myName ? initials(myName) : 'U'}
            </Avatar>
          </IconButton>
        </Box>

        <Menu
          id="profile-menu"
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleProfileMenuClose}
          onClick={handleProfileMenuClose}
          PaperProps={{
            elevation: 0,
            sx: {
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
              mt: 1.5,
              '& .MuiAvatar-root': {
                width: 32,
                height: 32,
                ml: -0.5,
                mr: 1,
              },
              '&:before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <MenuItem onClick={handleProfileMenuClose}>
            <Avatar sx={{ bgcolor: user ? avatarColor(user.user_id) : undefined, fontSize: 13, fontWeight: 700 }}>
              {myName ? initials(myName) : <AccountCircle />}
            </Avatar>
            <ListItemText primary={myName} secondary={user?.email} />
          </MenuItem>
          <Divider />
          <MenuItem onClick={() => setNameOpen(true)}>
            <ListItemIcon>
              <BadgeOutlined fontSize="small" />
            </ListItemIcon>
            Your name
          </MenuItem>
          <MenuItem onClick={handleProfileMenuClose}>
            <ListItemIcon>
              <Settings fontSize="small" />
            </ListItemIcon>
            Settings
          </MenuItem>
          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <Logout fontSize="small" />
            </ListItemIcon>
            Logout
          </MenuItem>
        </Menu>

        <Menu
          id="notification-menu"
          anchorEl={notifAnchorEl}
          open={Boolean(notifAnchorEl)}
          onClose={handleNotifMenuClose}
          PaperProps={{ sx: { minWidth: 300, maxWidth: 380, mt: 1.5 } }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          {taskAlerts.map((n) => {
            const actor = n.actor_user_id ? actorNames.get(n.actor_user_id) || 'Someone' : 'Someone';
            return (
              <MenuItem key={n.notification_id} onClick={() => openTaskAlert(n.task_id, n.notification_id)} sx={{ alignItems: 'flex-start', whiteSpace: 'normal' }}>
                <ListItemIcon sx={{ mt: 0.5 }}>
                  <TaskAlt fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <>
                      <strong>{describeNotification(n.kind, actor)}</strong> {n.task_title}
                    </>
                  }
                  secondary={timeAgo(n.create_time)}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </MenuItem>
            );
          })}
          {unreadTaskAlerts > taskAlerts.length && (
            <MenuItem onClick={() => goTo('/tasks')}>
              <ListItemText inset secondary={`${unreadTaskAlerts - taskAlerts.length} more task update${unreadTaskAlerts - taskAlerts.length === 1 ? '' : 's'}`} />
            </MenuItem>
          )}
          {unreadTaskAlerts > 0 && (
            <MenuItem onClick={markAllTaskAlertsRead} dense>
              <ListItemText inset secondary="Mark task updates as read" />
            </MenuItem>
          )}
          {tasksDue > 0 && (
            <MenuItem onClick={() => goTo('/tasks')}>
              <ListItemIcon>
                <TaskAlt fontSize="small" color={taskSummary?.overdue ? 'error' : 'warning'} />
              </ListItemIcon>
              <ListItemText
                primary={[
                  taskSummary?.overdue ? `${taskSummary.overdue} overdue` : null,
                  taskSummary?.due_today ? `${taskSummary.due_today} due today` : null,
                ].filter(Boolean).join(' · ')}
                secondary="Your tasks"
              />
            </MenuItem>
          )}
          {(unreadTaskAlerts > 0 || tasksDue > 0) && (pendingOrderRequests > 0 || unreadContactMessages > 0) && <Divider />}
          {notificationTotal === 0 && tasksDue === 0 && (
            <MenuItem disabled>
              <ListItemText primary="You're all caught up" />
            </MenuItem>
          )}
          {pendingOrderRequests > 0 && (
            <MenuItem onClick={() => goTo('/orders/requests')}>
              <ListItemIcon>
                <Storefront fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={`${pendingOrderRequests} pending order request${pendingOrderRequests === 1 ? '' : 's'}`}
                secondary="Review orders from the online store"
              />
            </MenuItem>
          )}
          {unreadContactMessages > 0 && (
            <MenuItem onClick={() => goTo('/customers/messages')}>
              <ListItemIcon>
                <Email fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={`${unreadContactMessages} unread message${unreadContactMessages === 1 ? '' : 's'}`}
                secondary="From the storefront contact form"
              />
            </MenuItem>
          )}
        </Menu>
        <NameDialog
          open={nameOpen}
          onClose={() => setNameOpen(false)}
          user={user}
          self
          onSaved={(saved) => user && updateUser({ ...user, display_name: saved.display_name })}
        />
      </Toolbar>
    </AppBar>
  );
};

/** Who did what in the bell's task updates. */
const actorIds = (alerts: { actor_user_id: string | null }[]) => alerts.map((a) => a.actor_user_id);
